// @ts-nocheck
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import { FBoxContainer, FHButton } from '../../../../base';
import LinkedClaims from './LinkedClaims';
import ClaimOverviewInfo from './ClaimOverviewInfo';
import ClaimOverviewStatus from './ClaimOverviewStatus';
import ClaimOverviewFinancial from './ClaimOverviewFinancial';
import EligibilityOfBenefits from './EligibilityOfBenefits';
import FollowUpNotes from './FollowUpNotes';
import { FORTA_BLUE } from '../../../../../utils/colors';
import { claimsActions } from '../../../../../store';
import { areUserRolesIn, formatEntityId, history } from '../../../../../helpers';
import { ENTITY_PREFIX, Roles } from '../../../../../constants';
import ErrorAlertIcon from '../../../../svgs/ErrorAlertIcon';

import type { RootState } from '../../../../../store';

function ClaimOverview() {
  const dispatch = useDispatch();
  const { claimId } = useParams();
  const { user } = useSelector((state: RootState) => state.auth);
  const { current: claim, loading: claimsLoading } = useSelector(
    (state: RootState) => state.claims,
  );
  const [isEditingInfo, setIsEditingInfo] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (!claimId) return;
    setIsEditingInfo(false);
  }, [claimId]);

  const handleCancelUpdates = () => {
    setIsEditingInfo(false);
    if (claim) {
      dispatch(claimsActions.updateState({ key: 'current', data: { ...claim } }));
    }
  };

  const handleEdit = () => {
    setIsEditingInfo(true);
  };

  return (
    <Box>
      <LinkedClaims />
      <FBoxContainer sx={{ flexGrow: 0, boxShadow: 'none' }} dataTestId="claimOverviewContainer">
        {claim?.descendants && claim?.descendants?.length > 0 && (
          <Box sx={{ display: 'flex', mb: 2 }}>
            <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" color={FORTA_BLUE.primary} />
            <Typography
              textAlign={'left'}
              alignSelf="start"
              lineHeight={'20px'}
              ml={0.5}
              mt={-0.1}
              sx={{ cursor: 'pointer' }}
              color={FORTA_BLUE.primary}
              onClick={() => {
                history.navigate(
                  `/billings/claims/${claim.descendants[claim.descendants.length - 1].id}`,
                );
              }}
            >
              Only the corrected claim{' '}
              {claim.descendants[claim.descendants.length - 1].id == null ? null : (
                <strong>
                  {formatEntityId(
                    claim.descendants[claim.descendants.length - 1].id,
                    ENTITY_PREFIX.Claim,
                  )}
                </strong>
              )}{' '}
              can be edited
            </Typography>
          </Box>
        )}
        <ClaimOverviewStatus
          isEditingInfo={isEditingInfo}
          allErrors={errors}
          onErrorUpdate={(err) => {
            setErrors((prev) => ({ ...prev, ...err }));
          }}
        />
        <EligibilityOfBenefits isEditingInfo={isEditingInfo} />
        <ClaimOverviewInfo
          isEditingInfo={isEditingInfo}
          allErrors={errors}
          onErrorUpdate={(err) => {
            setErrors((prev) => ({ ...prev, ...err }));
          }}
        />
        <ClaimOverviewFinancial
          isEditingInfo={isEditingInfo}
          allErrors={errors}
          onErrorUpdate={(err) => {
            setErrors((prev) => ({ ...prev, ...err }));
          }}
        />

        {!claim?.invoice_id && areUserRolesIn(user?.roles, [Roles.Biller]) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            {!isEditingInfo && !claim?.descendants?.length && (
              <FHButton data-testid="coEditInfoButton" onClick={handleEdit}>
                Edit info
              </FHButton>
            )}
            {isEditingInfo && (
              <>
                <FHButton
                  disabled={claimsLoading}
                  onClick={handleCancelUpdates}
                  data-testid="coDoneEditingButton"
                >
                  Done Editing
                </FHButton>
              </>
            )}
          </Box>
        )}
      </FBoxContainer>
      <FBoxContainer sx={{ flexGrow: 0, mt: 3, boxShadow: 'none' }}>
        <FollowUpNotes />
      </FBoxContainer>
    </Box>
  );
}

export default ClaimOverview;
